// src/features/hotels/components/HotelList.tsx

import React from 'react';
import {
  List,
  TextField,
  FunctionField,
  useTranslate,
  ReferenceInput,
  ListProps,
} from 'react-admin';
import { Box, Typography, useTheme } from '@mui/material';
import { Hotel as HotelIcon } from '@mui/icons-material';
import CustomDataGrid from '../../../components/common/CustomDatagrid';
import HotelActions from './HotelActions';
import { useHotelPermissions } from '../hooks/useHotelPermissions';
import { Hotel } from '../types';
import CustomListToolbar from '@/components/common/CustomListToolbar';
import { CustomSelectInputFilter, CustomTextInputFilter } from '@/components/common/CustomFilters';

const HotelFilters = [
    <CustomTextInputFilter 
        key="search" 
        source="search" 
        label="resources.hotels.filters.search_placeholder" 
        alwaysOn 
      />,


  <ReferenceInput 
    key="groupId"
    source="groupId" 
    reference="groups" 
    label="resources.hotels.fields.group"
    alwaysOn
  >
   
    <CustomSelectInputFilter  optionText="name" size='small'/>
  </ReferenceInput>,

];

// Componente principal de lista
export const HotelList: React.FC<ListProps> = (props) => {
  const translate = useTranslate();
  const theme = useTheme();
  const { canList, canEdit, canCreate } = useHotelPermissions();

  // Si no tiene permisos, mostrar mensaje
  if (!canList) {
    return (
      <Box 
        sx={{ 
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh'
        }}
      >
        <HotelIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          {translate('ra.page.forbidden', 'Acceso Denegado')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.hotels.messages.access_denied', 'No tienes permisos para ver los hoteles.')}
        </Typography>
      </Box>
    );
  }

  return (
    <List 
      {...props}
      filters={HotelFilters}
      actions={<CustomListToolbar
        showFilterButton={false}
        showCreate={canCreate}
        createLabel={translate('resources.hotels.actions.create', 'Nuevo Hotel')}
      />}
      perPage={25}
      sort={{ field: 'name', order: 'ASC' }}
      title={translate('resources.hotels.titles.list', 'Gestión de Hoteles')}
      empty={
        <Box 
          sx={{ 
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <HotelIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {translate('resources.hotels.messages.no_hotels', 'No hay hoteles registrados')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {translate('resources.hotels.messages.no_hotels_hint', 'Comienza creando tu primer hotel')}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ mt: 2 }}>
        <CustomDataGrid
          rowClick={canEdit ? 'edit' : undefined}
          bulkActionButtons={false}
          sx={{
            '& .RaDatagrid-headerCell': {
              fontWeight: 600,
            },
          }}
        >
          {/* Acciones */}
          <FunctionField
            label=""
            render={(record: Hotel) => <HotelActions record={record} />}
            sortable={false}
          />

          {/* Nombre del hotel */}
          <TextField 
            source="name" 
            label={translate('resources.hotels.fields.name', 'Nombre')} 
            sortable={true}
            sx={{
              '& .column-name': {
                fontWeight: 500,
              },
            }}
          />

          {/* Grupo */}
          <FunctionField
            label={translate('resources.hotels.fields.group', 'Grupo')}
            render={(record: Hotel) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                
                <Typography variant="body2">
                  {record.groupName || record.group?.name || '-'}
                </Typography>
              </Box>
            )}
            sortable={false}
          />

          {/* Código de instalación */}
          <FunctionField
            label={translate('resources.hotels.fields.installationCode', 'Código')}
            render={(record: Hotel) => (
              record.installationCode ? (
                <Typography variant="body2">
                  {record.installationCode}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.disabled">-</Typography>
              )
            )}
            sortable={false}
          />

          {/* Ubicación */}
          <TextField 
            source="address" 
            label={translate('resources.hotels.fields.address', 'Dirección')} 
            sortable={false}
            sx={{
              '& .RaDatagrid-cellContent': {
                maxWidth: 200,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
            }}
          />

         

          {/* Fecha de creación */}
          <FunctionField
            label={translate('resources.hotels.fields.createdAt', 'Creado')}
            render={(record: Hotel) => (
              <Typography variant="body2" color="text.secondary">
                {new Date(record.createdAt).toLocaleDateString()}
              </Typography>
            )}
            sortable={true}
          />

          
        </CustomDataGrid>
      </Box>
    </List>
  );
};

export default HotelList;